// CorridorDownloadsPage — Self-Service Corridor Builds
// ====================================================
// Abschnitt „App (Rider)" der docs/corridor-selfservice-spec.md:
// GPX hochladen (+ Breite 10/20/30 + Name) → Job-Liste mit Live-Status
// (Polling: queued mit Warteschlangen-Position → „Building corridor…
// Schritt x/6" → done mit Download) → Fehlerfall mit Retry.
//
// Design folgt dem NAVI-Bereich (Sky = Planning-Farbe, dunkles turbo-900).
// Polling nur solange ein Job aktiv ist (5 s), sonst ruhig. Die Datei-
// Downloads sind normale <a href> auf /api/corridors/:id/files/* — Auth
// läuft übers HttpOnly-Cookie (gleicher Browser-Kontext wie der Login).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type CorridorJob,
  type CorridorManifest,
  type CorridorNaviSlot,
  deleteCorridor,
  deleteCorridorNavi,
  expiresInText,
  formatBytes,
  getCorridorManifest,
  listCorridors,
  naviExpiresInText,
  retryCorridor,
  uploadCorridor,
} from '../lib/corridorApi';
import { dropCorridorOverlays } from '../lib/corridorOverlays';
import { parseImportedGpx } from '../lib/gpxFormat';
import { listCustomTrails, saveCustomTrail } from '../lib/customTrails';

const WIDTH_OPTIONS = [10, 20, 30] as const;
const POLL_MS = 5000;

/**
 * Die hochgeladene GPX zusaetzlich als Custom Tour ablegen.
 *
 * Warum: ein Korridor ohne die zugehoerige Strecke ist sinnlos — wer einen
 * Korridor baut, will seine Route auch auf der Karte sehen. Vorher musste
 * man dieselbe Datei zweimal hochladen (einmal hier, einmal unter Custom
 * Tours). Die GPX liegt im Upload-Moment ohnehin schon als Text im Browser,
 * das lokale Speichern kostet also keinen zweiten Transfer.
 *
 * Fail-soft: schlaegt das Parsen oder Speichern fehl, ist der Korridor-
 * Upload trotzdem durch — die Tour fehlt dann eben, das ist kein Grund,
 * dem User einen roten Fehler zu zeigen. Rueckgabe sagt dem Aufrufer nur,
 * ob er es erwaehnen kann.
 */
async function saveGpxAsCustomTour(
  gpxXml: string,
  name: string,
): Promise<'saved' | 'exists' | 'failed'> {
  try {
    const parsed = parseImportedGpx(gpxXml);
    // Elevation pro Punkt mitnehmen, wenn die GPX sie hat — sonst faellt
    // das Hoehenprofil auf SRTM zurueck und weicht sichtbar ab
    // (siehe Wave 14cf-fix5 im Custom-Tours-Import).
    const coords = parsed.coords.map((c, i): [number, number] | [number, number, number] => {
      const ele = parsed.elevations[i];
      return ele != null && Number.isFinite(ele) ? [c[0], c[1], ele] : c;
    });
    // Doppelte vermeiden: gleicher Name und Laenge auf 10 m genau heisst,
    // die Tour ist schon da (z. B. weil der Build wiederholt wurde).
    const existing = await listCustomTrails();
    if (
      existing.some(
        (t) => t.name === name && Math.abs(t.lengthM - parsed.lengthM) < 10,
      )
    ) {
      return 'exists';
    }
    await saveCustomTrail({
      name,
      coords,
      lengthM: parsed.lengthM,
      difficulty: 'unknown',
      source: 'corridor',
    });
    return 'saved';
  } catch (err) {
    console.info('[corridor] GPX konnte nicht als Custom Tour abgelegt werden', err);
    return 'failed';
  }
}

export function CorridorDownloadsPage() {
  const [jobs, setJobs] = useState<CorridorJob[]>([]);
  const [workerEnabled, setWorkerEnabled] = useState(true);
  // Navi-Slot: pro User darf genau EIN Korridor Navigationsdaten haben.
  const [naviSlot, setNaviSlot] = useState<CorridorNaviSlot | null>(null);
  // Soll dieser Build Navigation bekommen? Default ja — „Route berechnen"
  // im eigenen Korridor ist der ganze Sinn der Übung.
  const [withNavi, setWithNavi] = useState(true);
  // Explizite Freigabe des belegten Slots (User muss aktiv zustimmen, dass
  // die Navigationsdaten des anderen Korridors gelöscht werden).
  const [releaseSlot, setReleaseSlot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ---- Upload-Formular ----
  const [gpxXml, setGpxXml] = useState<string | null>(null);
  const [gpxFileName, setGpxFileName] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [widthKm, setWidthKm] = useState<number>(10);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listCorridors();
      setJobs(data.jobs);
      setWorkerEnabled(data.workerEnabled);
      setNaviSlot(data.naviSlot ?? null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial laden + Polling nur solange etwas läuft/wartet. Das Interval
  // hängt an `jobs` — sobald der letzte aktive Job done/error ist, räumt
  // der Effekt das Polling automatisch ab.
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, refresh]);

  const onPickFile = async (file: File | null) => {
    setUploadError(null);
    setUploadNote(null);
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('GPX ist zu groß (max. ~20 MB).');
      return;
    }
    const text = await file.text();
    // Inhalts-Check statt accept-Filter (s. Kommentar am input): eine echte
    // GPX hat ein <gpx-Root und <trkpt-Punkte — egal wie die Datei heißt.
    if (!/<gpx[\s>]/i.test(text.slice(0, 4000)) || !/<trkpt[\s>]/i.test(text)) {
      setUploadError(
        `„${file.name}" sieht nicht nach einer GPX mit Track aus (kein <gpx>/<trkpt> gefunden).`,
      );
      return;
    }
    setGpxXml(text);
    setGpxFileName(file.name);
    // Namensvorschlag aus dem Dateinamen ("3PBR_5_mangart.gpx" → "3PBR 5 mangart").
    if (!name.trim()) {
      setName(
        file.name
          .replace(/\.gpx$/i, '')
          .replace(/[_-]+/g, ' ')
          .trim()
          .slice(0, 60),
      );
    }
  };

  const onSubmit = async () => {
    setUploadError(null);
    setUploadNote(null);
    if (!gpxXml) {
      setUploadError('Bitte zuerst eine GPX-Datei wählen.');
      return;
    }
    if (name.trim().length < 2) {
      setUploadError('Name bitte mit 2–60 Zeichen.');
      return;
    }
    setUploading(true);
    try {
      const holderId = naviSlot?.heldBy?.id;
      const res = await uploadCorridor({
        gpxXml,
        name: name.trim(),
        widthKm,
        navi: withNavi,
        ...(withNavi && releaseSlot && holderId ? { releaseNaviFrom: holderId } : {}),
      });
      // Strecke lokal als Custom Tour ablegen — sonst muesste der User
      // dieselbe GPX ein zweites Mal unter Custom Tours importieren, nur
      // damit seine Route auf der Karte erscheint.
      const tour = await saveGpxAsCustomTour(gpxXml, name.trim());
      const tourNote =
        tour === 'saved'
          ? ' Die Strecke liegt jetzt auch unter Custom Tours.'
          : tour === 'exists'
            ? ' Die Strecke liegt bereits unter Custom Tours.'
            : '';
      if (res.deduped) {
        setUploadNote(
          'Diese GPX + Breite gibt es schon — bestehendes Paket wird angezeigt.' + tourNote,
        );
      } else {
        setUploadNote('Hochgeladen — der Build startet gleich.' + tourNote);
      }
      setGpxXml(null);
      setGpxFileName(null);
      setName('');
      setReleaseSlot(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refresh();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto bg-turbo-900 safe-top">
      <div className="p-5 pb-10 max-w-xl mx-auto">
        <div className="mb-5 flex items-center gap-3">
          <Link
            to="/navi"
            className="text-sky-400/80 text-sm no-underline active:text-sky-300"
            aria-label="Zurück zu NAVI"
          >
            ← NAVI
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-sky-400">
            Corridor Downloads
          </h1>
        </div>

        {!workerEnabled && (
          <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-amber-200 text-sm">
            Der Build-Server ist gerade pausiert — Uploads werden gespeichert
            und gebaut, sobald er wieder läuft.
          </div>
        )}

        {/* ---- Upload-Karte ---- */}
        <div className="rounded-2xl border border-sky-500/30 bg-turbo-700/30 p-4 mb-6">
          <div className="text-white font-semibold mb-3">
            Neuen Korridor bauen
          </div>

          {/* KEIN accept-Attribut: iOS Safari ignoriert Endungen in accept und
              graut Dateien ohne registrierten GPX-MIME-Typ aus (iOS kennt
              application/gpx+xml nicht) → GPX-Dateien waren nicht wählbar.
              Stattdessen alles zulassen und den INHALT in onPickFile prüfen. */}
          <input
            ref={fileInputRef}
            id="corridor-gpx-input"
            type="file"
            className="hidden"
            onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
          />
          <label
            htmlFor="corridor-gpx-input"
            className={`block rounded-xl border border-dashed p-4 text-center text-sm cursor-pointer transition-colors ${
              gpxFileName
                ? 'border-sky-500/60 bg-sky-500/10 text-sky-200'
                : 'border-white/25 text-white/60 active:border-sky-500/60'
            }`}
          >
            {gpxFileName ? (
              <>
                <span className="font-medium">{gpxFileName}</span>
                <span className="block text-white/40 mt-1">
                  Antippen zum Ändern
                </span>
              </>
            ) : (
              <>GPX-Datei wählen</>
            )}
          </label>

          <div className="mt-3">
            <label className="block text-white/60 text-xs mb-1" htmlFor="corridor-name">
              Name
            </label>
            <input
              id="corridor-name"
              type="text"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Alpencross 2026"
              className="w-full rounded-xl bg-turbo-900/70 border border-white/15 px-3 py-2.5 text-white placeholder-white/30 outline-none focus:border-sky-500/60"
            />
          </div>

          <div className="mt-3">
            <div className="text-white/60 text-xs mb-1">
              Korridor-Breite (je Seite der Route)
            </div>
            <div className="grid grid-cols-3 gap-2">
              {WIDTH_OPTIONS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWidthKm(w)}
                  className={`rounded-xl py-2.5 text-sm font-semibold border transition-colors ${
                    widthKm === w
                      ? 'bg-sky-500/20 border-sky-400/70 text-sky-200'
                      : 'bg-turbo-900/50 border-white/15 text-white/60 active:border-sky-500/40'
                  }`}
                >
                  {w} km
                </button>
              ))}
            </div>
            <div className="text-white/35 text-[11px] mt-1.5">
              Schmaler = kleineres Paket &amp; schnellerer Build. 10 km reichen
              meist fürs Rennen.
            </div>
          </div>

          {/* ---- Navigation (Routing-Daten) ---- */}
          <div className="mt-3 rounded-xl border border-white/12 bg-turbo-900/40 p-3">
            <button
              type="button"
              onClick={() => setWithNavi((v) => !v)}
              className="flex w-full items-start gap-2.5 text-left"
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[12px] font-bold ${
                  withNavi
                    ? 'border-sky-400/70 bg-sky-500/25 text-sky-200'
                    : 'border-white/25 text-transparent'
                }`}
                aria-hidden
              >
                ✓
              </span>
              <span className="min-w-0">
                <span className="block text-white text-sm font-semibold">
                  Navigation im Korridor („Route berechnen")
                </span>
                <span className="block text-white/45 text-[11px] mt-0.5">
                  Die Routing-Daten bleiben{' '}
                  <span className="text-amber-300/90">
                    {naviSlot?.retentionDays ?? 14} Tage
                  </span>{' '}
                  auf dem Server, danach werden sie gelöscht — fürs Routen
                  danach den Korridor einfach neu bauen. Karte, Höhenlinien und
                  POIs im heruntergeladenen Paket bleiben davon unberührt.
                </span>
              </span>
            </button>

            {withNavi && naviSlot?.heldBy && (
              <div className="mt-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-amber-200 text-[11px]">
                Navigationsdaten hat gerade{' '}
                <span className="font-semibold">„{naviSlot.heldBy.name}"</span>
                {naviExpiresInText(naviSlot.heldBy.expiresAt)
                  ? ` (${naviExpiresInText(naviSlot.heldBy.expiresAt)})`
                  : ''}
                . Es kann immer nur EIN Korridor navigieren.
                <button
                  type="button"
                  onClick={() => setReleaseSlot((v) => !v)}
                  className={`mt-2 block w-full rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                    releaseSlot
                      ? 'border-red-400/60 bg-red-500/20 text-red-200'
                      : 'border-amber-400/50 bg-amber-500/15 text-amber-100'
                  }`}
                >
                  {releaseSlot
                    ? `✓ „${naviSlot.heldBy.name}" gibt die Navigation ab`
                    : `„${naviSlot.heldBy.name}" freigeben und hier navigieren`}
                </button>
              </div>
            )}
          </div>

          {uploadError && (
            <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-2.5 text-red-300 text-sm">
              {uploadError}
            </div>
          )}
          {uploadNote && (
            <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-2.5 text-emerald-300 text-sm">
              {uploadNote}
            </div>
          )}

          <button
            type="button"
            disabled={uploading || !gpxXml}
            onClick={() => void onSubmit()}
            className="mt-4 w-full rounded-xl bg-sky-500 py-3 font-semibold text-white disabled:opacity-40 active:bg-sky-400 transition-colors"
          >
            {uploading ? 'Wird hochgeladen…' : 'Korridor bauen'}
          </button>
        </div>

        {/* ---- Job-Liste ---- */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-white font-semibold">Deine Pakete</div>
          <button
            type="button"
            onClick={() => void refresh()}
            className="text-sky-400/80 text-sm active:text-sky-300"
          >
            Aktualisieren
          </button>
        </div>

        {loading && <div className="text-white/40 text-sm py-6 text-center">Lade…</div>}
        {loadError && !loading && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-red-300 text-sm">
            {loadError}
          </div>
        )}
        {!loading && !loadError && jobs.length === 0 && (
          <div className="text-white/40 text-sm py-6 text-center">
            Noch keine Pakete — lad oben eine GPX hoch.
          </div>
        )}

        <div className="flex flex-col gap-3">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onChanged={refresh} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function JobCard({ job, onChanged }: { job: CorridorJob; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [manifest, setManifest] = useState<CorridorManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  // Wave 14dz: Loesch-Bestaetigung IN der Seite statt window.confirm().
  //
  // User-Report: "auf Paket loeschen passiert gar nichts". Ursache: in einer
  // iOS-Homescreen-PWA (display: standalone) sind window.confirm/alert/prompt
  // nicht verlaesslich — WebKit unterdrueckt die Dialoge dort, confirm()
  // liefert dann sofort false und der Handler steigt still aus. Kein Request,
  // keine Fehlermeldung, nichts. Also eigener Zwei-Schritt-Confirm, der
  // ueberall funktioniert.
  const [pending, setPending] = useState<null | 'delete' | 'navi'>(null);
  // Fehler aus Aktionen (Loeschen, Retry) — BEWUSST getrennt von
  // manifestError, das nur im aufgeklappten Download-Block gerendert wurde
  // und damit bei eingeklappter Karte unsichtbar blieb.
  const [actionError, setActionError] = useState<string | null>(null);

  const toggleDownloads = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (!manifest) {
      try {
        setManifest(await getCorridorManifest(job.id));
        setManifestError(null);
      } catch (err) {
        setManifestError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const onRetry = async () => {
    setBusy(true);
    try {
      await retryCorridor(job.id);
      await onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doReleaseNavi = async () => {
    setPending(null);
    setBusy(true);
    setActionError(null);
    try {
      await deleteCorridorNavi(job.id);
      await dropCorridorOverlays(job.regionSlug);
      await onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setPending(null);
    setBusy(true);
    setActionError(null);
    try {
      await deleteCorridor(job.id);
      await dropCorridorOverlays(job.regionSlug);
      await onChanged();
    } catch (err) {
      // z. B. 409 job_running oder 404 not_found — der User sah bisher gar
      // nichts, weil der Fehler im eingeklappten Manifest-Block landete.
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-turbo-700/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-white font-semibold leading-tight truncate">{job.name}</div>
          <div className="text-white/40 text-xs mt-0.5">
            {job.widthKm} km/Seite ·{' '}
            {new Date(job.createdAt).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </div>
        </div>
        <StatusChip status={job.status} />
      </div>

      {job.status === 'queued' && (
        <div className="mt-3 text-white/60 text-sm">
          In der Warteschlange
          {job.queuePosition > 0 ? ` — ${job.queuePosition} Job${job.queuePosition === 1 ? '' : 's'} vor dir` : ' — startet gleich'}
          …
        </div>
      )}

      {job.status === 'running' && (
        <div className="mt-3">
          <div className="text-sky-200 text-sm mb-1.5">
            Building corridor… Schritt {job.progress.step}/{job.progress.total}
            {job.progress.label ? ` · ${job.progress.label}` : ''}
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-sky-400 transition-all duration-700"
              style={{ width: `${Math.max(3, job.progress.pct)}%` }}
            />
          </div>
        </div>
      )}

      {job.status === 'error' && (
        <div className="mt-3">
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-2.5 text-red-300 text-sm">
            Build fehlgeschlagen.
            <button
              type="button"
              className="ml-2 underline text-red-200"
              onClick={() => setShowError((v) => !v)}
            >
              {showError ? 'Details ausblenden' : 'Details'}
            </button>
            {showError && job.error && (
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap text-[11px] text-red-200/80">
                {job.error}
              </pre>
            )}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onRetry()}
            className="mt-2 rounded-xl bg-sky-500/20 border border-sky-400/50 px-4 py-2 text-sky-200 text-sm font-semibold disabled:opacity-40"
          >
            Nochmal versuchen
          </button>
        </div>
      )}

      {job.status === 'done' && (
        <div className="mt-3">
          {/* Registry-Integration: der fertige Korridor ist ab jetzt auch eine
              Region im Tiles-Manifest → auf der Offline-Seite herunterladen
              und als aktive Region wählen, dann nutzt die KARTE Basemap/
              Contours/Relief/Routing aus diesem Paket. */}
          <Link
            to="/offline"
            className="block w-full rounded-xl border border-emerald-400/50 bg-emerald-500/15 py-2.5 text-center font-semibold text-emerald-200 no-underline active:bg-emerald-500/25 mb-2"
          >
            Auf der Karte nutzen → Offline-Download
          </Link>
          {/* 72-h-Retention: der Server räumt das Paket danach automatisch weg. */}
          {expiresInText(job.expiresAt) && (
            <div className="text-amber-300/80 text-xs mb-2">
              ⏳ {expiresInText(job.expiresAt)} — danach wird das Paket
              automatisch gelöscht (einfach neu bauen, wenn du es wieder brauchst).
            </div>
          )}
          <button
            type="button"
            onClick={() => void toggleDownloads()}
            className="w-full rounded-xl bg-sky-500 py-2.5 font-semibold text-white active:bg-sky-400 transition-colors"
          >
            {expanded ? 'Downloads ausblenden' : `Downloads anzeigen (${job.artifactCount} Dateien)`}
          </button>

          {expanded && (
            <div className="mt-3">
              {manifestError && (
                <div className="text-red-300 text-sm">{manifestError}</div>
              )}
              {!manifest && !manifestError && (
                <div className="text-white/40 text-sm">Lade Manifest…</div>
              )}
              {manifest && (
                <>
                  <div className="text-white/50 text-xs mb-2">
                    {manifest.meta?.lengthKm ? `${manifest.meta.lengthKm} km Route · ` : ''}
                    gesamt {formatBytes(manifest.totalBytes)}
                  </div>
                  <div className="flex flex-col divide-y divide-white/5 rounded-xl border border-white/10 overflow-hidden">
                    {manifest.artifacts.map((a) => (
                      <a
                        key={a.file}
                        href={a.url}
                        download
                        className="flex items-center justify-between gap-3 px-3 py-2.5 bg-turbo-900/40 text-sm no-underline active:bg-sky-500/10"
                      >
                        <span className="text-white/85 truncate">{a.file}</span>
                        <span className="text-white/40 shrink-0">{formatBytes(a.sizeBytes)}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {job.status === 'expired' && (
        <div className="mt-3 rounded-xl border border-white/12 bg-turbo-900/40 p-2.5 text-white/55 text-xs">
          Das Download-Paket wurde nach Ablauf der Frist gelöscht. Die
          Navigationsdaten laufen noch — „Route berechnen" funktioniert
          weiterhin, solange die Frist unten läuft.
        </div>
      )}

      {/* ---- Navigation (Routing-Daten, eigene 14-Tage-Frist) ---- */}
      {job.navi && job.navi.status !== 'none' && job.navi.status !== 'expired' && (
        <div className="mt-3 rounded-xl border border-sky-400/25 bg-sky-500/5 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sky-200 text-sm font-semibold">
              🧭 Navigation{' '}
              {job.navi.status === 'pending' ? '(wird gebaut)' : 'aktiv'}
            </div>
            {job.navi.areas > 1 && (
              <span className="shrink-0 rounded-full border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-[11px] text-sky-200">
                {job.navi.areas} Bereiche
              </span>
            )}
          </div>
          {job.navi.status === 'active' && (
            <div className="text-amber-300/85 text-[11px] mt-1.5">
              ⏳ {naviExpiresInText(job.navi.expiresAt) ?? 'läuft'} — danach
              werden die Navigationsdaten gelöscht (Korridor einfach neu bauen).
            </div>
          )}
          {job.navi.areas > 1 && (
            <div className="text-white/45 text-[11px] mt-1.5">
              Der Korridor ist zu lang für einen Routing-Graphen und deshalb in{' '}
              {job.navi.areas} Bereiche unterteilt (auf der Karte sichtbar).
              Start und Ziel müssen im selben Bereich liegen.
            </div>
          )}
          {job.navi.status === 'active' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setActionError(null);
                setPending('navi');
              }}
              className="mt-2.5 w-full rounded-lg border border-white/20 px-3 py-2 text-xs font-semibold text-white/60 active:border-red-400/50 active:text-red-300 disabled:opacity-40"
            >
              Navigationsdaten löschen (Slot freigeben)
            </button>
          )}
        </div>
      )}

      {pending && (
        <div className="mt-3 rounded-xl border border-red-400/50 bg-red-500/10 p-3">
          <div className="text-red-100 text-sm">
            {pending === 'navi'
              ? `Navigationsdaten von „${job.name}" löschen? „Route berechnen" geht in diesem Korridor danach nicht mehr — Karte und POIs bleiben. Dafür ist der Navi-Slot wieder frei.`
              : job.navi && job.navi.status === 'active'
                ? `„${job.name}" komplett vom Server löschen? Damit sind auch die Navigationsdaten weg — der Navi-Slot wird frei.`
                : `„${job.name}" samt allen Dateien vom Server löschen?`}
          </div>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void (pending === 'navi' ? doReleaseNavi() : doDelete())}
              className="flex-1 rounded-lg bg-red-500/80 px-3 py-2 text-sm font-semibold text-white active:bg-red-400 disabled:opacity-40"
            >
              {busy ? 'Lösche…' : 'Ja, löschen'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPending(null)}
              className="flex-1 rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white/70 disabled:opacity-40"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {actionError && (
        <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-2.5 text-red-300 text-sm">
          {actionError}
        </div>
      )}

      {job.status !== 'running' && !pending && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setActionError(null);
              setPending('delete');
            }}
            className="text-white/35 text-xs underline active:text-red-300 disabled:opacity-40"
          >
            {job.status === 'expired' ? 'Eintrag löschen' : 'Paket löschen'}
          </button>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: CorridorJob['status'] }) {
  const map: Record<CorridorJob['status'], { label: string; cls: string }> = {
    queued: { label: 'Wartet', cls: 'bg-white/10 text-white/60 border-white/15' },
    running: { label: 'Baut…', cls: 'bg-sky-500/15 text-sky-300 border-sky-400/40' },
    done: { label: 'Fertig', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40' },
    error: { label: 'Fehler', cls: 'bg-red-500/15 text-red-300 border-red-400/40' },
    expired: {
      label: 'Paket gelöscht',
      cls: 'bg-white/10 text-white/50 border-white/15',
    },
  };
  const { label, cls } = map[status];
  return (
    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}
